import { EntrepriseOptionsEnum } from '@/app/plateformebeneficiaire/core/models/enums/entrepriseOption.enum';
import { ListExternalApplicationsEnum } from '@/app/plateformebeneficiaire/core/models/enums/listExternalApplications.enum';
import { EntrepriseOptionService } from '@/app/plateformebeneficiaire/core/service/entrepriseOption.service';
import { UserService } from '@/app/plateformebeneficiaire/core/service/user.service';
import { ExternalApplicationsUtils } from '@/app/plateformebeneficiaire/core/utils/externalApplications.utils';
import { Component, inject, effect, computed, signal, Output, Input, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { MessageService } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { StepsModule } from 'primeng/steps';
import { StepperModule } from 'primeng/stepper';
import { FileUploadModule } from 'primeng/fileupload';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CommonModule } from '@angular/common';
import { DocumentService } from '@/app/plateformebeneficiaire/core/service/document.service';

@Component({
  selector: 'app-create-document-dialog',
  imports: [
    ButtonModule,
    DialogModule,
    CheckboxModule,
    FormsModule,
    StepperModule,
    StepsModule,
    FileUploadModule,
    ProgressSpinnerModule,
    CommonModule
  ],
  templateUrl: './create-document-dialog.component.html',
  styleUrl: './create-document-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateDocumentDialogComponent {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() reset = new EventEmitter<void>();
  @Output() created = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  constructor(private config: PrimeNG, private messageService: MessageService, private changeDetector: ChangeDetectorRef) {
    effect(() => {
      const userInfo = this.#userInfoQuery.data();
      if (userInfo && userInfo.phoneNumber && !this.documentTel) {
        this.documentTel = userInfo.phoneNumber;
      }
    });
  }

  
  #userService = inject(UserService);
  #entrepriseOptionsService = inject(EntrepriseOptionService);
  #documentService = inject(DocumentService);
  #externalApplicationsUtils = inject(ExternalApplicationsUtils);

  #userInfoQuery = this.#userService.getUserInfos();
  #entrepriseOptionsQuery = this.#entrepriseOptionsService.listEntrepriseOptions();

  isLoadingUserInfo = computed(() => this.#userInfoQuery.isLoading() ?? false);

  checked: boolean = false;
  activeIndex = signal(0);
  stepItem = signal(1);
  selectedApp = '';
  documentTel: string = '';

  files: any[] = [];
  isUploading: boolean = false;

  externalApplications = computed(() => {
    let x = this.#entrepriseOptionsQuery.data()?.find(
      (x: any) => x.key == EntrepriseOptionsEnum.ListExternalLinks
    )?.value ?? "[]";
    return JSON.parse(x);
  })

  filteredApplications = computed(() => {
    const enabledApps = this.externalApplications();
    const userLinks = this.#userInfoQuery.data()?.externalLinks ?? [];

    return Object.values(ListExternalApplicationsEnum)
      .filter(key => enabledApps.includes(key))
      .map(key => {
        const { name, description } = this.#externalApplicationsUtils.getDisplayValue(key);
        const externalLink = userLinks.find(link => link.system === key);
        return {
          name,
          key,
          description,
          folderNumber: externalLink?.external_dossier_id || null,
          connected: !!externalLink
        };
      })
      .filter(app => app.connected);
  });

  /** Retourne les initiales d'un nom d'application (max 2 lettres) */
  getInitials(name: string): string {
    return name
      .split(/\s+/)
      .filter(w => w.length > 0)
      .map(w => w[0].toUpperCase())
      .slice(0, 2)
      .join('');
  }

  /** Validation du numéro de portable (06 ou 07 suivi de 8 chiffres, 10 chiffres au total) */
  validatorPhone(phone: string): boolean {
    return /^0[67]\d{8}$/.test(phone.trim());
  }

  showDialog() {
    this.visible = true;
  }

  resetDialog() {
    this.activeIndex.set(0);
    this.stepItem.set(1);
    this.checked = false;
    this.selectedApp = '';
    // Préserver le téléphone s'il vient des infos utilisateur
    const userInfo = this.#userInfoQuery.data();
    this.documentTel = userInfo?.phoneNumber || '';
    this.files = [];
    this.isUploading = false;
    this.changeDetector.detectChanges();
  }

  closeDialog() {
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.reset.emit();
  }

  goToStep(newStepNumber: number) {
    this.activeIndex.set(newStepNumber);
  }

  onCheckboxChange(appKey: string, checked: boolean): void {
    if (checked) {
      this.selectedApp = appKey;
    } else {
      this.selectedApp = '';
    }
  }

  upload(){
    this.isUploading = true;
    this.#documentService.uploadDocuments.mutate({
      fichiers: this.files,
      application: this.selectedApp,
      telBeneficiaire: this.documentTel
      }, {
        onSuccess: () => {
          this.isUploading = false;
          this.messageService.add({ key: 'global', severity: 'success', summary: 'Succès', detail: 'Document(s) enregistré(s) !' });
          this.resetDialog();
          this.closeDialog();
          this.reset.emit();  
          this.created.emit();
        },
        onError: (error) => {
          this.isUploading = false;
          this.messageService.add({ key: 'global', severity: 'error', summary: 'Erreur', detail: error.message });
        }
    });
  }

  onSelectedFiles(event: any) {
    this.files = event.currentFiles;
  }

  formatSize(bytes: any) {
    const k = 1024;
    const dm = 3;
    const sizes = this.config.translation.fileSizeTypes ?? [];
    if (bytes === 0) {
        return `0 ${sizes[0] || 'B'}`;
    }

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

    return `${formattedSize} ${sizes[i]}`;
  }
}
